import {
  LitElement, html, nothing,
  type CSSResultGroup,
  type TemplateResult,
} from 'lit';
import { property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import {
  VirtualScrollController,
  type VirtualRange,
} from '../utils/virtual-scroll.controller.js';
import styles from './styles.js';

// ── Item shape that the default renderer understands ──────────────────────────

export interface VirtualScrollerItem {
  /** Unique identifier (used as key and for aria-selected). */
  id?:      string | number;
  /** Primary display text. */
  label:    string;
  /** Secondary text shown below label. */
  subtitle?:string;
}

/**
 * uwc-virtualscroller
 *
 * A high-performance windowed list that renders only the items visible inside
 * its scroll viewport.  The underlying `VirtualScrollController` can be
 * re-used in any other component (dropdown, datatable, autocomplete).
 *
 * @attr items        - Array of items to render.
 * @attr item-size    - Pixel height of one item. Default: 48.
 * @attr scroll-height - Height of the scroll viewport (px). Default: 400.
 * @attr buffer       - Extra items rendered above / below viewport. Default: 4.
 * @attr loading      - Show skeleton loading state.
 * @attr empty-message - Message shown when items is empty. Default: "No items".
 * @attr selected-id  - id of the currently selected item.
 *
 * @property renderItem - Optional function `(item, index) => TemplateResult`.
 *   When provided, replaces the built-in item renderer.
 *
 * @method scrollToIndex(n, behavior?) - Programmatically scroll to an index.
 * @method scrollToTop(behavior?)      - Scroll to the start.
 * @method scrollToBottom(behavior?)   - Scroll to the end.
 *
 * @fires uwc-range-change  - When the rendered range changes. Detail: VirtualRange.
 * @fires uwc-item-click    - When a default-rendered item is clicked. Detail: { item, index }.
 *
 * @slot header - Content rendered above the list inside the border.
 * @slot footer - Content rendered below the list inside the border.
 * @slot empty  - Custom empty-state content (replaces empty-message).
 *
 * @csspart viewport    - The scroll container.
 * @csspart item        - Each default-rendered item row.
 *
 * @cssprop --uwc-vs-border          - Outer border. Default: 1px solid border-token.
 * @cssprop --uwc-vs-radius          - Border radius.
 * @cssprop --uwc-vs-bg              - Background color.
 * @cssprop --uwc-vs-item-height     - Item row height. Default: 3rem.
 * @cssprop --uwc-vs-item-padding-x  - Item horizontal padding.
 * @cssprop --uwc-vs-item-hover-bg   - Item hover background.
 * @cssprop --uwc-vs-item-selected-bg - Selected item background.
 * @cssprop --uwc-vs-scrollbar       - Scrollbar thumb color.
 */
export class UwcVirtualScroller<
  T extends VirtualScrollerItem = VirtualScrollerItem
> extends LitElement {

  static override styles: CSSResultGroup = [styles];

  // ── Properties ───────────────────────────────────────────────────

  @property({ type: Array })              items:         T[]    = [];
  @property({ type: Number, attribute: 'item-size' })
                                          itemSize:      number = 48;
  @property({ type: Number, attribute: 'scroll-height' })
                                          scrollHeight:  number = 400;
  @property({ type: Number })            buffer:        number = 4;
  @property({ type: Boolean })           loading:       boolean = false;
  @property({ attribute: 'empty-message' }) emptyMessage = 'No items';
  @property({ attribute: 'selected-id' }) selectedId?:  string | number;

  /**
   * Custom item renderer.  When set, the built-in item markup is replaced.
   * The function receives the item and its absolute list index.
   *
   * @example
   *   el.renderItem = (item, index) => html`<div>${index}: ${item.label}</div>`;
   */
  renderItem?: (item: T, index: number) => TemplateResult;

  // ── Internal ─────────────────────────────────────────────────────

  @state() private _focusedIndex = -1;

  private _vs = new VirtualScrollController<T>(this, {
    onRangeChange: (range: VirtualRange) => {
      this.dispatchEvent(new CustomEvent('uwc-range-change', {
        bubbles: true, composed: true,
        detail: range,
      }));
    },
  });

  // ── Lifecycle ─────────────────────────────────────────────────────

  override willUpdate(changed: Map<PropertyKey, unknown>): void {
    if (changed.has('items'))    this._vs.setItems(this.items);
    if (changed.has('itemSize')) this._vs.setItemSize(this.itemSize);
    if (changed.has('buffer'))   this._vs.setBuffer(this.buffer);
  }

  override firstUpdated(): void {
    const vp = this.shadowRoot!.querySelector<HTMLElement>('.uwc-vs__viewport')!;
    this._vs.attach(vp);
  }

  // ── Public methods ────────────────────────────────────────────────

  /** Scroll so that item at `index` is visible. */
  scrollToIndex(index: number, behavior: ScrollBehavior = 'auto'): void {
    this._vs.scrollToIndex(index, behavior);
  }

  scrollToTop(behavior: ScrollBehavior = 'auto'): void {
    this._vs.scrollToTop(behavior);
  }

  scrollToBottom(behavior: ScrollBehavior = 'auto'): void {
    this._vs.scrollToBottom(behavior);
  }

  // ── Keyboard navigation ───────────────────────────────────────────

  private _onKeydown(e: KeyboardEvent): void {
    const total = this._vs.totalCount;
    if (!total) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this._focusedIndex = Math.min(this._focusedIndex + 1, total - 1);
      this._vs.scrollToIndex(this._focusedIndex, 'smooth');
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this._focusedIndex = Math.max(this._focusedIndex - 1, 0);
      this._vs.scrollToIndex(this._focusedIndex, 'smooth');
    } else if (e.key === 'Home') {
      e.preventDefault();
      this._focusedIndex = 0;
      this._vs.scrollToTop();
    } else if (e.key === 'End') {
      e.preventDefault();
      this._focusedIndex = total - 1;
      this._vs.scrollToBottom();
    } else if ((e.key === 'Enter' || e.key === ' ') && this._focusedIndex >= 0) {
      e.preventDefault();
      this._selectItem(this.items[this._focusedIndex], this._focusedIndex);
    }
  }

  private _selectItem(item: T, index: number): void {
    this.selectedId = item.id;
    this._focusedIndex = index;
    this.dispatchEvent(new CustomEvent('uwc-item-click', {
      bubbles: true, composed: true,
      detail: { item, index },
    }));
  }

  // ── Renderers ─────────────────────────────────────────────────────

  private _renderDefaultItem(item: T, index: number): TemplateResult {
    const selected = item.id !== undefined && item.id === this.selectedId;
    const focused  = index === this._focusedIndex;
    return html`
      <div
        part="item"
        class="uwc-vs__item"
        role="option"
        aria-selected=${selected}
        data-focused=${focused}
        style=${styleMap({ height: `${this.itemSize}px` })}
        @click=${() => this._selectItem(item, index)}
      >
        <div>
          <div>${item.label}</div>
          ${item.subtitle
            ? html`<div style="font-size:.75rem;color:#9ca3af;margin-top:1px;">${item.subtitle}</div>`
            : nothing}
        </div>
      </div>
    `;
  }

  private _renderSkeleton(): TemplateResult {
    const count = Math.ceil(this.scrollHeight / this.itemSize);
    return html`
      ${Array.from({ length: count }, () => html`
        <div class="uwc-vs__skeleton-item">
          <div class="uwc-vs__skeleton-bar" style="width:40%;flex-shrink:0;"></div>
          <div class="uwc-vs__skeleton-bar" style="flex:1;"></div>
        </div>
      `)}
    `;
  }

  private _renderEmpty(): TemplateResult {
    return html`
      <div class="uwc-vs__empty" role="status" aria-live="polite">
        <slot name="empty">${this.emptyMessage}</slot>
      </div>
    `;
  }

  // ── Render ────────────────────────────────────────────────────────

  override render(): TemplateResult {
    const hasHeader = !!this.querySelector('[slot="header"]');
    const hasFooter = !!this.querySelector('[slot="footer"]');

    const wrapperStyles = styleMap({
      height: `${this.scrollHeight}px`,
    });

    let listContent: TemplateResult | typeof nothing = nothing;

    if (this.loading) {
      listContent = this._renderSkeleton();
    } else if (this._vs.totalCount === 0) {
      listContent = this._renderEmpty();
    } else {
      const renderer = this.renderItem
        ? this.renderItem.bind(this)
        : this._renderDefaultItem.bind(this);

      listContent = html`
        <div
          class="uwc-vs__spacer"
          style=${styleMap({ height: `${this._vs.paddingBefore}px` })}
          aria-hidden="true"
        ></div>
        ${this._vs.visibleItems.map(({ item, index }) => renderer(item, index))}
        <div
          class="uwc-vs__spacer"
          style=${styleMap({ height: `${this._vs.paddingAfter}px` })}
          aria-hidden="true"
        ></div>
      `;
    }

    return html`
      <div class="uwc-vs" style=${wrapperStyles}>
        ${hasHeader ? html`<div class="uwc-vs__header"><slot name="header"></slot></div>` : nothing}

        <div
          part="viewport"
          class="uwc-vs__viewport"
          role="listbox"
          tabindex="0"
          aria-label="Virtual list"
          aria-rowcount=${this._vs.totalCount}
          @keydown=${this._onKeydown}
        >
          ${listContent}
        </div>

        ${hasFooter ? html`<div class="uwc-vs__footer"><slot name="footer"></slot></div>` : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-virtualscroller': UwcVirtualScroller; }
}
